<template>
  <section class="invite-user">
    <PageHeader showBreadcrumbs page-title="Invite user">
      <template #buttons>
        <CloseButton :onClick="close" />
      </template>
    </PageHeader>
    <div class="block-wrapper">
      <div class="row">
        <div class="col-4">
          <div class="row">
            <div class="col-12">
              <div class="mb-3 text-center">

                <UserAvatar
                  v-model="createInvite.avatarFile"
                  :userAvatar="userAvatar"
                  @resetAvatarInputAfterModalClose="resetAvatarInputAfterModalClose"
                  @uploadCroppedAvatar="uploadCroppedAvatar"
                  @onAvatarFile="onAvatarFile"
                  />

              </div>
              <FormGenerator class="mt-4" :data="userCheckBoxes.form" :showFormControls="false"
                @updateItem="userCheckBoxesHandler" />
            </div>
          </div>
        </div>
        <div class="col-8">
          <div class="row">
            <div class="col-12">
              <PageHeader page-title="User details" wrapperClassName="" titleClassName="block-title" />
              <FormGenerator class="mt-4" :data="userDetails.form" :showFormControls="false"
                @submit="submitUserDetails" ref="detailsForm" />
            </div>
            <div class="col-12">
              <div class="break-line" />
            </div>
            <div class="col-12">
              <PageHeader page-title="Profile details" wrapperClassName="" titleClassName="block-title" />
              <FormGenerator class="mt-4" :data="createInvite.form" :showFormControls="false"
                @submit="submitUserProfile" @input="inputFormField" ref="profileForm" />
            </div>
            <div class="col-12">
              <div class="break-line" />
            </div>
            <div class="col-12">
              <PageHeader page-title="Contact details" titleClassName="block-title" wrapperClassName="mb-0">
                <template #buttons>
                  <b-button class="button justify-content-center chevron-button" style="height: 32px;" variant="plain"
                    @click="toggleShowingContactDetails">
                    <span class="material-symbols-outlined material-symbols-fill-1 mr-1 ml-2"
                      :style="{ transform: !isShowContactDetails ? 'rotate(180deg)' : 'none', transition: '0.2s' }">
                      keyboard_arrow_down
                    </span>
                  </b-button>
                </template>
              </PageHeader>
              <b-collapse v-model="isShowContactDetails">
                <FormGenerator class="mt-4" :data="userContactDetails.form" :showFormControls="false"
                  @submit="submitUserContactDetails" ref="contactDetailsForm" />
              </b-collapse>
            </div>
            <div class="col-12">
              <div class="break-line" />
            </div>
            <div class="col-12">
              <NewUserProfileSkills v-model="userSkills" @update="updateUserSkills"
                :showSkillsError="showSkillsError" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <StickyButtonsWrapper isShowAnimation :loading="isLoading" v-if="isShowStickyButtons">
      <b-button class="button title-container__controls-item justify-content-center min-width-130" variant="outline"
        @click="close">
        Cancel
      </b-button>
      <b-button class="button justify-content-center min-width-190" variant="primary" @click="submit">
        Invite User
      </b-button>
    </StickyButtonsWrapper>
  </section>
</template>

<script>
import PageHeader from '@components/PageHeader.vue'
import UserAvatar from '@components/UserAvatar.vue'
import FormGenerator from '@components/FormGenerator'
import NewUserProfileSkills from '@components/NewUserProfileSkills'
import UserDetailsMixin from '@mixins/UserDetailsMixin'
import UserMixin from '@mixins/UserMixin'
import StickyButtonsWrapper from '@components/forms/StickyButtonsWrapper'
import CloseButton from '@components/common/CloseButton.vue'
import UserContactDetailsMixin from '@mixins/UserContactDetailsMixin'

export default {
  name: 'InviteUser',
  components: { PageHeader, UserAvatar, FormGenerator, NewUserProfileSkills, StickyButtonsWrapper, CloseButton },
  emits: ['close', 'loadingChange'],
  mixins: [UserDetailsMixin, UserMixin, UserContactDetailsMixin],
  data () {
    return {
      isLoading: false,
      userAvatar: '',
      userCheckBoxes: {
        form: {
          show_on_widget: {
            type: 'checkbox',
            name: 'show_on_widget',
            label: 'Show on ',
            labelLink: ' available profiles',
            labelLinkTo: '/available-profiles',
            value: false,
            class: 'col-12 user-profile-checkbox-wrapper',
            info: 'Enabling this will make this user’s profile visible on your available profiles page.'
          },
          is_contact_person: {
            type: 'checkbox',
            name: 'is_contact_person',
            label: 'Contact person',
            value: false,
            class: 'col-12 user-profile-checkbox-wrapper user-profile-checkbox-wrapper__contact'
          }
        }
      },
      userSkills: [],
      showSkillsError: false,
      userDetailsData: null,
      userProfileData: null,
      userContactDetailsData: null,
      isStickyButtonsVisible: false,
      isShowStickyButtons: true,
      isShowContactDetails: true
    }
  },
  async created () {
    this.hideContactDetailsEmailField()
  },
  watch: {
    'userCheckBoxes.form.show_on_widget.value' (value) {
      const fields = ['years_of_experience', 'availability', 'seniority_lvl', 'english_lvl', 'position']

      if (value) {
        fields.forEach(field => {
          if (this.createInvite.form[field]) {
            this.createInvite.form[field].validation = [{ name: 'required' }]
            this.createInvite.form[field].label += ' *'
            this.createInvite.form[field].value = this.userProfileData[field]?.value
          }
        })
      } else {
        fields.forEach(field => {
          if (this.createInvite.form[field]) {
            this.createInvite.form[field].validation = []
            this.createInvite.form[field].label = this.createInvite.form[field].label.replace(' *', '')
            this.createInvite.form[field].value = this.userProfileData[field]?.value
          }
        })
      }
    }
  },
  methods: {
    // Override fetchAllData from UserMixin to add loading state management
    async fetchAllData () {
      this.isLoading = true
      this.$emit('loadingChange', true)

      try {
        // Call the parent fetchAllData method from UserMixin
        await this.$options.mixins.find(mixin => mixin.methods?.fetchAllData)?.methods.fetchAllData.call(this)
      } catch (error) {
        console.error('InviteUser: Error in fetchAllData:', error)
        throw error
      } finally {
        this.isLoading = false
        this.$emit('loadingChange', false)
      }
    },
    onAvatarFile (e) {
      this.handleAvatarFile(e, 'createInvite')
    },
    toggleShowingContactDetails () {
      this.isShowContactDetails = !this.isShowContactDetails
    },
    close () {
      this.isShowStickyButtons = false
      setTimeout(() => {
        this.$emit('close')
      })
    },
    async uploadCroppedAvatar (data) {
      this.userAvatar = await this.fileToBase64(data)
    },
    userCheckBoxesHandler (name, { value }) {
      this.userCheckBoxes.form[name].value = value
    },
    submitUserDetails (data) {
      this.userDetailsData = data
    },
    submitUserProfile (data) {
      this.userProfileData = data
    },
    submitUserContactDetails (data) {
      delete data.email
      this.userContactDetailsData = data
    },
    updateUserSkills (data) {
      this.userSkills = data
    },
    inputFormField (fields) {
      if (fields.employment_type.value && fields.employment_type.value.name === 'partner') {
        fields.partner.hidden = false
      } else {
        fields.partner.hidden = true
      }

      this.userProfileData = fields
    },
    submit () {
      if (this.userSkills.some(item => !item.skill || !item.scale)) {
        this.showSkillsError = true
      } else {
        this.showSkillsError = false
      }

      this.$refs.detailsForm.submit()
      this.$refs.profileForm.submit()
      this.$refs.contactDetailsForm.submit()

      if (this.userDetailsData && this.userProfileData) {
        const userData = {
          ...this.userCheckBoxes.form,
          ...this.userProfileData,
          ...this.userContactDetailsData,
          ...this.userDetailsData,
          skills: { name: 'skills', value: [...this.userSkills.map(item => ({ id: item.skill.id, name: item.skill.name, value: item.scale }))] }
        }
        let dataToSend = this.prepareDataForRequest(userData)
        dataToSend.avatar = this.userAvatar
        this.isLoading = true
        this.$emit('loadingChange', true)
        this.axios
          .post('/api/user-invitation/create', dataToSend,
            {
              headers: {
                Authorization: 'Bearer' + localStorage.getItem('access_token')
              }
            })
          .then(({ data }) => {
            this.$snotify.success(
              data.statusText,
              'Success',
              this.$store.state.snotify
            )
            this.close()
          })
          .catch(error => {
            this.errorNotifications(error)
          })
          .finally(() => {
            this.isLoading = false
            this.$emit('loadingChange', false)
          })
      }
    }
  }
}
</script>
